import {
  EVENT_CHAINING_ACTION_TO_ANALYTICS_NAME,
  TimeslotFilterCriteria,
  eventTypeToDisplayName,
} from 'app/enums'
import {getIds, logError, omit, partial, prefixKeys} from 'util/common'

import bowser from 'bowser'
import {getMobilizeAnonymousId} from 'util/anonymous-id'
import {getQueryParams} from 'util/url'

if (!window.analytics && process.env.NODE_ENV !== 'test') {
  logError('Failed to initialize Segment analytics object')
}

function maybeIdentifyUser(
  segmentUserData,
  integrationOptions = {identifyToChameleon: false}
) {
  if (!window.analytics) {
    return
  }
  const maybeIntegrations = integrationOptions.identifyToChameleon
    ? null
    : {Chameleon: false}
  identify(segmentUserData.id, omit(segmentUserData, 'id'), maybeIntegrations)
}

function stripEmptyFields(obj) {
  return omit(obj, (value) => value === '')
}

function excludeNullAndUndefinedFields(obj) {
  return omit(obj, (value) => value === null || value === undefined)
}

// Pass this as the base of the `options` argument so that we can override the anonymous id to be
// the one we generate at the edge instead of a Segment-generated UUID.
// Also send along "context", to be included with all FE track and identify calls.
// https://segment.com/docs/sources/website/analytics.js/#setting-the-anonymous-id
function getDefaultSegmentOptions(anonymousId) {
  return {
    anonymousId: anonymousId || getMobilizeAnonymousId(),
    context: getSegmentContext(),
  }
}

const getSegmentContext = () => {
  // Provides some info about the user's device, leveraging Bowser ua parsing for now.
  // Once UA Client Hints (https://github.com/WICG/ua-client-hints) become available,
  // we should leverage those in addition to or instead of ua parsing.
  //
  // Per conversation with Segment support, this `context` dictionary is actually
  // free-form and can contain whatever info we'd like to send.
  //
  // References:
  //  1) https://segment.com/docs/connections/spec/common/#context
  //  2) http://lancedikson.github.io/bowser/docs/global.html#ParsedResult
  //  3) https://medium.com/@galmeiri/get-ready-for-chrome-user-agent-string-phase-out-c6840da1c31e

  try {
    const parser = bowser.getParser(window.navigator.userAgent)
    const uaInfo = parser.getResult()
    return {
      device: {
        manufacturer: uaInfo.platform.vendor,
        model: uaInfo.platform.model,
        type: uaInfo.platform.type,
      },
      os: {
        name: uaInfo.os.name,
        version: uaInfo.os.version,
        versionName: uaInfo.os.versionName,
      },
      browser: {
        name: uaInfo.browser.name,
        version: uaInfo.browser.version,
      },
      engine: {
        name: uaInfo.engine.name,
        version: uaInfo.engine.version,
      },
      // Override the segment `page` defaults because it unexpectedly tries to use the canonical
      // url, which we don't update on soft navigation
      page: {
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
      },
    }
  } catch (e) {
    console.warn('Could not parse user agent', e)
    return {}
  }
}

// Establishes a relationship between the current anonymous id and the provided
// user id. Should be called whenever action taken on the client creates a user
// record on the backend (among other uses; for full documentation see
// https://segment.com/docs/connections/spec/identify/).
// NB: calls to this function often have significant downstream implications; eg,
// the ids being treated as one in Amplitude from here on out. Use with care!
function identify(userId, traits, maybeIntegrations) {
  _identify({userId, traits, maybeIntegrations})
}

// Call identify without a user ID. Useful where the user doesn't have their id
// yet, or we don't know it in this context, but want to associate some properties
// with the user.
function identifyAnonymous(traits, maybeIntegrations) {
  _identify({traits, maybeIntegrations})
}

const _identify = (identifyArgs) => {
  if (!window.analytics) {
    return
  }
  const {userId, traits, maybeIntegrations} = identifyArgs
  const cleanedTraits = stripEmptyFields(excludeNullAndUndefinedFields(traits))
  const options = {
    ...(maybeIntegrations ? {integrations: maybeIntegrations} : {}),
    ...getDefaultSegmentOptions(),
  }
  if (userId) {
    window.analytics.identify(`${userId}`, cleanedTraits, options)
    return
  }
  window.analytics.identify(cleanedTraits, options)
}

// This tracks real-world data of performance metrics client-side.
let didClientPerformanceOnLoadTrack = false
function trackClientPerformance(canonicalPath, retry = 0) {
  // We only want the on page load navigation for now.
  // This function can't handle measuring AJAX navigation currently.
  if (didClientPerformanceOnLoadTrack) {
    return
  }
  didClientPerformanceOnLoadTrack = true

  try {
    const observer = new window.PerformanceObserver((list) => {
      const navigationEntry = window.performance.getEntriesByType(
        'navigation'
      )[0]
      const paintEntries = window.performance.getEntriesByType('paint')

      if (navigationEntry && navigationEntry.duration) {
        const entries = {...navigationEntry.toJSON()}
        paintEntries.forEach((entry) => {
          entries[entry.name] = entry.startTime
        })

        track('performance', {
          canonicalPath,
          ...entries,
        })

        observer.disconnect()
      }
    })

    observer.observe({
      entryTypes: ['navigation', 'paint'],
    })
  } catch (ex) {
    // Do nothing. No big deal.
  }
}

function page() {
  if (!window.analytics) {
    return
  }
  window.analytics.page({}, getDefaultSegmentOptions())
  // send a pageview to our Facebook pixel
  // here (the id is supplied by the server at `window.__MLZ_FB_PIXEL_ID__`)
  if (window.fbq && window.__MLZ_FB_PIXEL_ID__) {
    window.fbq('trackSingle', window.__MLZ_FB_PIXEL_ID__, 'PageView')
  }
}

// Events in this list will never be sent to Amplitude at all
const AMPLITUDE_BLOCKED_EVENTS = new Set(['click', 'performance'])

function track(eventName, data, anonymousId) {
  if (!window.analytics) {
    return
  }

  const cleanedData = excludeNullAndUndefinedFields(data)
  window.analytics.track(eventName, cleanedData, {
    ...getDefaultSegmentOptions(anonymousId),
    integrations: {
      Amplitude:
        analyticsTracker.shouldSendSampleToAmplitude &&
        !AMPLITUDE_BLOCKED_EVENTS.has(eventName),
    },
  })
  // GTM one-off, because no apparent mapping to events in Segment
  if (window.dataLayer) {
    window.dataLayer.push({event: eventName})
  } else {
    logError('No GTM dataLayer found')
  }
}

function trackDashboardSignupModalOpened(timeslotId, eventId, org) {
  track('dashboardSignupModal.opened', {
    timeslotId: timeslotId,
    eventId: eventId,
    ...maybeGetOrgProperties(org),
  })
}

function maybeGetOrgProperties(org) {
  return {
    org_slug: org?.slug,
    org_name: org?.name,
    org_id: org?.id,
    org_type: org?.org_type,
  }
}

// Check if the given query params contain at least one UTM parameter.
function containsUtm(queryParams) {
  for (const param in queryParams) {
    if (param.startsWith('utm_')) {
      return true
    }
  }
  return false
}

// https://stackoverflow.com/a/326076; tested and works where other solutions did not
function inIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

function trackRegistrationCreated(data, filterParams) {
  const prefixedFilterParams = filterParams
    ? prefixKeys(filterParams, 'filter_params__')
    : {}
  const combinedParams = Object.assign({}, data, prefixedFilterParams)
  // Send the data thru Segment
  track('registration.created', combinedParams)

  // The Mobilize FB pixel is managed outside of Segment so we have to send this separately
  if (window.fbq && window.__MLZ_FB_PIXEL_ID__) {
    window.fbq(
      'trackSingle',
      window.__MLZ_FB_PIXEL_ID__,
      'CompleteRegistration',
      {content_name: window.location.pathname}
    )
  }

  // Also send a faux pageview to GA
  if (window.ga) {
    window.ga('send', 'pageview', `${window.location.pathname}/registered`)
  }
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_location: `${window.location.pathname}/registered`,
    })
  }
}

// Send form metadata to Segment when event is submitted with payment
function trackSignupWithPaymentAttempt(signupData, paymentData) {
  const analyticsData = {...signupData, payment: paymentData}
  track('signupWithPayment.attempted', analyticsData)
}

function trackSignupWithPaymentError(signupData, error) {
  const analyticsData = {...signupData, error: error}
  track('signupWithPayment.errored', analyticsData)
}

// Send form metadata to Segment when donation forms are loaded.
function trackOnlineActionFormLoaded(metadata) {
  track('onlineActionForm.loaded', metadata)
}

function trackPageLoaded(org, location, data) {
  const qs = getQueryParams(location.search)
  track('page.loaded', {
    ...data,
    search_string: location.search,
    has_utm: containsUtm(qs),
    page: analyticsTracker.latestPageName,
    ...maybeGetOrgProperties(org),
    previous_url: document.referrer,
    is_embed: inIframe(),
  })
}

function trackLeadCaptured(page, org, data = {}) {
  track('leadCaptureForm.submitted', {
    ...data,
    page,
    ...maybeGetOrgProperties(org),
  })
}

function trackFeedFiltered(source, filterParams, orgName, orgSlug) {
  track('feed.filtered', {
    source,
    ...filterParams,
    eventTypeAsString:
      filterParams.eventTypes?.length &&
      eventTypeToDisplayName(filterParams.eventTypes[0]),
    numEventTypesIncluded: filterParams.eventTypes?.length,
    orgName,
    orgSlug,
  })
}

// `unsampled` is there if you need to log 100% of all clicks.
// We currently only track a sample of clicks to Amplitude for
// cost-saving measures.
function trackButtonClicked(button, unsampled) {
  track(unsampled ? 'buttonUnsampled.clicked' : 'button.clicked', {
    button,
    page: analyticsTracker.latestPageName,
  })
}

// Tracks requests for login via email/magic link. Facebook login requests do not include an email
// because those requests are processed in a separate window and we only get data back on success.
// Facebook requests are also tracked as 'button.clicked' events.
function trackLoginRequested(data) {
  track('user.loginRequested', {
    ...data,
    page: analyticsTracker.latestPageName,
  })
}

function trackLoginSucceeded(data) {
  track('user.loginSucceeded', {...data, page: analyticsTracker.latestPageName})
}

function maybeGetHostedEventProperties(event) {
  return {
    event_name: event && event.name,
    event_description: event && event.description,
    event_pro_tips: event && event.pro_tips,
    event_image_url: event && event.image_url,
    event_timezone: event && event.timezone,
    event_location_name: event && event.location.name,
    event_address_line1: event && event.location.address_line1,
    event_address_line2: event && event.location.address_line2,
    event_city: event && event.location.city,
    event_state: event && event.location.state,
    event_zipcode: event && event.location.zipcode,
    event_accessibility_notes: event && event.accessibility_notes,
    event_accessibility_status: event && event.accessibility_status,
    event_timeslots:
      event &&
      event.timeslots.map((timeslot) => ({
        event_timeslot_id: timeslot.id,
        event_timeslot_start_at_naive: timeslot.starts_at_naive,
        event_timeslot_ends_at_naive: timeslot.ends_at_naive,
      })),
    event_num_timeslots: event && event.timeslots.length,
    event_contact_number: event && event.contact_number,
    event_host_first_name: event && event.host.first_name,
    event_host_last_name: event && event.host.last_name,
    event_host_email: event && event.host.email,
    event_visibility: event && event.visibility,
  }
}

function trackHostedEventSubmitted(data) {
  track('event.hostedEventSubmitted', {
    ...data,
    page: analyticsTracker.latestPageName,
  })
}

function maybeGetEventProperties(event) {
  return !event
    ? {}
    : {
        event_name: event.name,
        event_id: event.id,
        event_description: event.description,
        event_pro_tips: event.pro_tips,
        event_image_url: event.image_url,
        event_timezone: event.timezone,
        event_location_name: event.location_name,
        event_address_line1: event.address_line1,
        event_address_line2: event.address_line2,
        event_city: event.city,
        event_state: event.state,
        event_zipcode: event.zipcode,
        event_accessibility_notes: event.accessibility_notes,
        event_accessibility_status: event.accessibility_status,
        event_contact_number: event.contact_number,
        event_creator_first_name: event.creator && event.creator.first_name,
        event_creator_last_name: event.creator && event.creator.last_name,
        event_creator_email: event.creator && event.creator.email,
        event_visibility: event.visibility,
      }
}

function trackMessengerOpened(messengerTrackingData) {
  trackMessengerEvent('messenger.opened', messengerTrackingData)
}

function trackMessengerClosed(messengerTrackingData) {
  trackMessengerEvent('messenger.closed', messengerTrackingData)
}

function trackMessengerSentMessage(messengerTrackingData) {
  trackMessengerEvent('messenger.sentMessage', messengerTrackingData)
}

function trackMessengerEvent(analyticsEventName, messengerTrackingData) {
  const dataToSend = {
    ...maybeGetOrgProperties(messengerTrackingData.organization),
    ...maybeGetEventProperties(messengerTrackingData.event),
    event_campaign_id: messengerTrackingData.event_campaign_id,
    parent_page_name: messengerTrackingData.parentPageName,
    recipient_type: messengerTrackingData.recipientType,
  }

  if (messengerTrackingData.timeslots) {
    // Options: 'MULTIPLE_UPCOMING' | 'MULTIPLE_PAST' | 'SINGLE_UPCOMING' | 'SINGLE_PAST'
    // This is designed to reflect the real filtering options on the FE component.
    const cardinality =
      messengerTrackingData.timeslots.length === 1 ? 'SINGLE' : 'MULTIPLE'
    const temporality =
      messengerTrackingData.selectedTimeslotFilterCriteria ===
      TimeslotFilterCriteria.PAST_ONLY
        ? 'PAST'
        : 'UPCOMING'
    const timeslotFiltrationMode = `${cardinality}_${temporality}`

    dataToSend.timeslot_ids = getIds(messengerTrackingData.timeslots)
    dataToSend.timeslot_filtration_mode = timeslotFiltrationMode
  }

  if (messengerTrackingData.attendanceStatusesFilterCriteria) {
    dataToSend.attendance_statuses_filter_criteria =
      messengerTrackingData.attendanceStatusesFilterCriteria
  }

  if (messengerTrackingData.scopingTimeslots) {
    dataToSend.scoping_timeslot_ids = getIds(
      messengerTrackingData.scopingTimeslots
    )
  }

  // NB: may be unavailable (null) if feature is flagged off - in that case won't send anything.
  // We do want to support sends to 0 people, so a recipient count of 0 should get through.
  if (messengerTrackingData.recipientCount != null) {
    dataToSend.recipient_count = messengerTrackingData.recipientCount
  }

  if (messengerTrackingData.userInitiatedMessageId) {
    dataToSend.user_initiated_message_id =
      messengerTrackingData.userInitiatedMessageId
  }

  track(analyticsEventName, dataToSend)
}

function trackHostedEventCreated(data) {
  track('event.hostedEventCreated', {
    ...data,
    page: analyticsTracker.latestPageName,
  })
}

function trackDetailedHoursSubmitted(data) {
  track('participation.detailedHoursSubmitted', {
    ...data,
    page: analyticsTracker.latestPageName,
  })
}

function trackWelcomeMessageContactClickedForOrg(organization) {
  track(
    'dashboard_welcome_message_contact.clicked',
    maybeGetOrgProperties(organization)
  )
}

export const EventLocationErrorCode = {
  ADDRESS_LINE_1_INCOMPLETE: 'ADDRESS_LINE_1_INCOMPLETE',
  UNSUPPORTED_COUNTRY: 'UNSUPPORTED_COUNTRY',
  NOT_SPECIFIC_ENOUGH: 'NOT_SPECIFIC_ENOUGH',
  INVALID_ZIPCODE: 'INVALID_ZIPCODE',
}

function trackEventLocationError(organization, data) {
  track('error.eventLocationError', {
    ...data,
    ...maybeGetOrgProperties(organization),
  })
}

function maybeTrackZipcodeError(error, org, user, zipcode) {
  if (
    error.json &&
    error.json.error &&
    error.json.error.location &&
    error.json.error.location.zipcode
  ) {
    trackEventLocationError(org, {
      code: EventLocationErrorCode.INVALID_ZIPCODE,
      user_id: user ? user.id : null,
      zipcode: zipcode || '',
    })
  }
}

function trackLocationCleared() {
  track('location.cleared')
}

function trackLocationSuggested(data) {
  track('locationSuggestion.suggested', data)
}

function trackLocationSuggestionAccepted() {
  track('locationSuggestion.accepted')
}

function trackLocationSuggestionDismissed() {
  track('locationSuggestion.dismissed')
}

function trackDonateModalOpened(args) {
  const {event, organization, action} = args
  track('registration.donateModalOpened', {
    ...maybeGetEventProperties(event),
    ...maybeGetOrgProperties(organization),
    dialog_context: EVENT_CHAINING_ACTION_TO_ANALYTICS_NAME[action],
  })
}

function trackDonationModalDonateButtonClick(args) {
  const {event, organization, action, amount} = args

  track('actblueModalDonateButton.clicked', {
    ...maybeGetEventProperties(event),
    ...maybeGetOrgProperties(organization),
    dialog_context: EVENT_CHAINING_ACTION_TO_ANALYTICS_NAME[action],
    amount: amount,
  })
}

function trackFollowupModalShown({
  organization,
  count,
  action,
  inPersonCount,
  virtualCount,
  distinctDayCount,
}) {
  track('followupModal.shown', {
    eventCount: count,
    inPersonCount,
    virtualCount,
    distinctDayCount,
    context: EVENT_CHAINING_ACTION_TO_ANALYTICS_NAME[action],
    ...maybeGetOrgProperties(organization),
  })
}

const trackCurrentVolunteerChanged = partial(track, 'currentVolunteer.changed')

function trackExperimentAllocationByUser(experiment, allocation) {
  // This must be kept EXACTLY in sync with track_experiment_allocation_by_user in core.analytics
  track('experiment.allocated', {
    experiment_name: experiment.name,
    variant_name: allocation.variant.name,
    overridden: allocation.overridden,
    bucket: allocation.bucket,
  })
}

function trackPromotionCreated({promotingOrg, promotedOrg, context}) {
  track('promotion.created', {
    ...prefixKeys(maybeGetOrgProperties(promotingOrg), 'promoting_org__'),
    ...prefixKeys(maybeGetOrgProperties(promotedOrg), 'promoted_org__'),
    promotion_context: context,
  })
}

function trackAnonymousIdGenerated(anonymousId) {
  // Passing the anonymous id here prevents `track` from attempting to regenerate it if saving it as
  // a cookie failed (which would track again, creating an infinite loop)
  track('anonymous_id.generated', {}, anonymousId)
}

// Track actions within the self service org creation flow.
function trackSelfServiceAction(action, maybeProperties) {
  const properties = maybeProperties ? maybeProperties : {}
  track(`selfServiceOrgCreation.${action}`, properties)
}

// TODO(mime): This is a super basic generalized click tracking to
// get something working quickly. Eventually we can do a lot more
// granulated event tracking per component but this gets us 80% of
// the way without too much work.
// Brought this over from my 2020madness tracking work (and, in turn, 'all-the-things')
/**!
 * Modified from https://github.com/mimecuvalo/all-the-things/blob/master/packages/react-scripts/template/client/app/analytics.js
 * MIT License
 * Copyright Mime Čuvalo <mimecuvalo@gmail.com> [@mimecuvalo](https://github.com/mimecuvalo)
 */
const INTERESTING_ELEMENTS = ['a', 'button', 'img', 'input', 'label']
function handleMouseDown(evt) {
  let target = evt.target
  let localName = target.localName

  while (
    !INTERESTING_ELEMENTS.includes(localName) &&
    // These are Semantic UI classes that use <div>s. UGH.
    !(
      target.getAttribute &&
      ['listbox', 'option'].includes(target.getAttribute('role'))
    ) &&
    !(target.classList && target.classList.contains('checkbox'))
  ) {
    target = target.parentNode
    if (!target) {
      return
    }
    localName = target.localName
  }

  const aria = target.getAttribute('aria-describedby')
  const ariaEl = document.getElementById(aria)
  // TODO(mime): Can't wait for optional chaining...
  const tooltip = aria && ariaEl && ariaEl.innerText
  const name =
    tooltip ||
    target.getAttribute('data-track') ||
    target.getAttribute('aria-label') ||
    target.title ||
    target.innerText ||
    target.getAttribute('placeholder') ||
    target.getAttribute('name') ||
    target.getAttribute('alt') ||
    target.className ||
    target.id ||
    target.src ||
    target.srcset

  // Conditionally compile this code. Should not appear in production.
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[analytics] ${latestCanonicalPath} → ${localName} → ${name}`)
  }

  track('click', {
    canonicalPath: latestCanonicalPath,
    localName,
    name,
  })

  // If an exception is fired later on, Sentry will provide the latest
  // click events so that we get context on how the user arrived to this error.
  if (window.Sentry && window.Sentry.addBreadcrumb) {
    window.Sentry.addBreadcrumb({
      category: 'ui',
      message: `Click event: ${latestCanonicalPath}, ${localName}, ${name}`,
      level: 'info',
    })
  }
}

let latestCanonicalPath = ''
function setCurrentCanonicalPath(canonicalPath) {
  latestCanonicalPath = canonicalPath
}

// TODO(mime): more analytics methods should migrate into this class
class AnalyticsTracker {
  latestPageName = ''
  shouldSendSampleToAmplitude = false

  // For certain AMPLITUDE_SAMPLED_EVENTS we only want a certain percentage of
  // the events because of cost prohibitiveness.
  setShouldSendSampleToAmplitude(shouldSendSampleToAmplitude) {
    this.shouldSendSampleToAmplitude = shouldSendSampleToAmplitude
  }

  setPageName(analyticsPageName) {
    this.latestPageName = analyticsPageName
  }
}

// Exported just for testing purposes.
export const analyticsTracker = new AnalyticsTracker()

if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    window.addEventListener('mousedown', handleMouseDown)
  })
}

const analyticsExport = {
  identifyAnonymous,
  maybeIdentifyUser,
  maybeGetHostedEventProperties,
  maybeGetOrgProperties,
  maybeTrackZipcodeError,
  page,
  setCurrentCanonicalPath,
  track,
  trackDonationModalDonateButtonClick,
  trackDonateModalOpened,
  trackAnonymousIdGenerated,
  trackClientPerformance,
  trackCurrentVolunteerChanged,
  trackDashboardSignupModalOpened,
  trackExperimentAllocationByUser,
  trackPageLoaded,
  trackLeadCaptured,
  trackFeedFiltered,
  trackHostedEventCreated,
  trackDetailedHoursSubmitted,
  trackHostedEventSubmitted,
  trackLoginSucceeded,
  trackLoginRequested,
  trackOnlineActionFormLoaded,
  trackRegistrationCreated,
  trackSignupWithPaymentAttempt,
  trackSignupWithPaymentError,
  trackButtonClicked,
  trackWelcomeMessageContactClickedForOrg,
  trackLocationCleared,
  trackLocationSuggested,
  trackLocationSuggestionAccepted,
  trackLocationSuggestionDismissed,
  trackMessengerSentMessage,
  trackMessengerOpened,
  trackMessengerClosed,
  trackEventLocationError,
  trackFollowupModalShown,
  trackPromotionCreated,
  trackSelfServiceAction,
  tracker: analyticsTracker,
  EventLocationErrorCode,
}
export default analyticsExport
